import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useEditorStore } from "@/store/editorStore";
import { toast } from "sonner";

import TopBar from "@/components/editor/TopBar";
import LeftSidebar from "@/components/editor/LeftSidebar";
import DrawerPanel from "@/components/editor/DrawerPanel";
import CanvasStack from "@/components/editor/CanvasStack";
import ZoomSlider from "@/components/editor/ZoomSlider";
import PublishDrawer from "@/components/editor/PublishDrawer";
import DevicePreview from "@/components/editor/DevicePreview";
import ShareModal from "@/components/editor/ShareModal";
import WidgetModal from "@/components/editor/WidgetModal";

export default function Editor() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const setProject = useEditorStore((s) => s.setProject);
  const pages = useEditorStore((s) => s.pages);
  const projectName = useEditorStore((s) => s.projectName);

  const [activeTab, setActiveTab] = useState("templates");
  const [loading, setLoading] = useState(true);
  const [publishOpen, setPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        if (!cancelled) setProject(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load project");
        nav("/dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-save every 8s if dirty (cheap interval-based persistence)
  useEffect(() => {
    if (!projectId) return;
    const t = setInterval(() => {
      api.patch(`/projects/${projectId}`, { pages, name: projectName }).catch(() => {});
    }, 12000);
    return () => clearInterval(t);
  }, [projectId, pages, projectName]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#E5E7EB] font-display text-xl text-gray-500">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-[#E5E7EB]" data-testid="editor-root">
      <TopBar
        onOpenPublish={() => setPublishOpen(true)}
        onOpenPreview={() => setPreviewOpen(true)}
        onOpenShare={() => setShareOpen(true)}
        onOpenWidget={() => setWidgetOpen(true)}
      />
      <div className="flex flex-1 overflow-hidden relative">
        <LeftSidebar active={activeTab} onChange={setActiveTab} />
        <div className="w-80 bg-[#F9FAFB] border-r border-gray-200 shrink-0 overflow-hidden" data-testid="drawer-container">
          <DrawerPanel active={activeTab} />
        </div>
        <div className="flex-1 relative">
          <CanvasStack />
          <ZoomSlider />
        </div>
      </div>

      <PublishDrawer open={publishOpen} onClose={() => setPublishOpen(false)} />
      <DevicePreview open={previewOpen} onClose={() => setPreviewOpen(false)} />
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
      <WidgetModal open={widgetOpen} onClose={() => setWidgetOpen(false)} />
    </div>
  );
}
